import type { IAndroidPowerStatus } from '@/helpers/parseAndroidPowerStatus'
import { parseAndroidPowerStatus } from '@/helpers/parseAndroidPowerStatus'
import { isTauriRuntime } from '@/helpers/resolvedPlayerAppVersion'
import { getPlatform } from '@/platform'

export type { IAndroidPowerStatus }

/** Android-only: battery level and power source (battery vs mains) via `BatteryManager`. */
export async function fetchAndroidPowerStatus(): Promise<IAndroidPowerStatus | null> {
  if (!isTauriRuntime() || getPlatform() !== 'android') {
    return null
  }
  try {
    const { invoke } = await import('@tauri-apps/api/core')
    const raw = await invoke<unknown>('plugin:lucimark-android|getPowerStatus')
    return parseAndroidPowerStatus(raw)
  }
  catch {
    return null
  }
}

/**
 * Android-only: notified when mains power is plugged / unplugged (native `powerSourceChanged` event), so the
 * caller can send an early heartbeat instead of waiting for the next cycle. Resolves an unsubscribe fn.
 */
export async function listenAndroidPowerSourceChange(handler: (connected: boolean) => void): Promise<() => void> {
  if (!isTauriRuntime() || getPlatform() !== 'android') {
    return () => {}
  }
  try {
    const { addPluginListener } = await import('@tauri-apps/api/core')
    const listener = await addPluginListener('lucimark-android', 'powerSourceChanged', (payload: unknown) => {
      handler((payload as { connected?: unknown } | null)?.connected === true)
    })
    return () => {
      void listener.unregister().catch(() => {})
    }
  }
  catch {
    return () => {}
  }
}
